import { ConfigField, ConfigSlideId } from '@/eventConfig/types.ts';
import {
  ChangeEventHandler,
  FC,
  MouseEventHandler,
  useEffect,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { translate } from '@/translation/translate.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { isNil } from 'ramda';
import { FieldType, FormGetFieldStatusFn, FormGetFieldValueFn, FormOnChangeFn } from '@/form/FormProvider.tsx';
import { useUiEvent } from '@/util/useUiEvent.ts';
import { useFieldValidation } from '../_pipeline/useValidateField';

interface ButtonsInputProps {
  field: ConfigField;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
  getFieldValue: FormGetFieldValueFn;
}

export const ButtonsInput: FC<ButtonsInputProps> = ({ field, slideId, getFieldStatus, onChange, getFieldValue }) => {
  const fieldStatus = getFieldStatus(field.name);
  const { handleUiEvent } = useUiEvent();
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);

  const fieldHint = fieldStatus?.hint;
  const value = String(fieldStatus?.value?.raw ?? '');
  const htmlId = 'id_field_' + field.name;
  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required' || v.type === 'required_if');
  const isValid = fieldStatus?.isValid ?? !isRequired;

  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = () => {
    if (field.click && handleUiEvent) {
      handleUiEvent(field.click);
    }
  };

  // Forgive me Father for I have sinned. I will not clean this *** up
  // It will stay bad, I do not have the stregth anymore.
  let buttons = field.options ?? [];

  const colWidth = field.options ? Math.floor(12 / field.options.length) : 12;

  // Buttons can be hidden in UK when "Title" becomes "Prof." we need to then conditionally
  // ask for a Gender, which is implemented with hiding these buttons and some weird shit
  // @TODO: dont anymore... why?
  const hasLink = !isNil(field.link);
  let isHidden = false;
  let linkedValue: FieldType | undefined;
  if (hasLink) {
    const [_slideId, fieldName] = (field.link?.field ?? '').split('.');
    linkedValue = getFieldStatus(fieldName)?.value?.normalized;
    // Now Each of the "Buttons" has options. Each option means a gender value.
    // If a Buttons "options" has length 1 this means that it only has one Gender.
    // Which then means we do NOT show this button row. Because the gender is already fixed
    // as in "title" == "mr." which means gender is male.
    // Lets fkn do this in the most fkd up way possible. Here goes:

    const buttonRelatingToLinkedFieldValue = buttons.find(({ label }) => label === linkedValue);

    if (!buttonRelatingToLinkedFieldValue || !buttonRelatingToLinkedFieldValue?.options) {
      isHidden = true;
    }

    if ((buttonRelatingToLinkedFieldValue?.options || []).length < 2) {
      isHidden = true;
    } else {
      // And for the most interesting part: IF WE HAVE 2 OR MORE OPTIONS in the value
      // That is linked by the button link relations
      // THEN We ignore the "field.options" button definitions that we are given
      // and instead render the linked fields options as buttons.

      // OG Pull request:
      // https://bitbucket.dkmsnet.lan/projects/WEB/repos/ereg/pull-requests/111/overview
      // I AM NOT TAKING ANY RESPONSIBVILTY FOR ANY OF THE CODE
      // IN THIS FILE

      // Commit:
      // https://bitbucket.dkmsnet.lan/projects/WEB/repos/ereg/commits/a97b7e4e7d6e3a2609278ed455427815bd732cc2
      // Here is the OG commit if you want to start checking  this out. I am only the
      // one who has to clean this shit up.
      buttons = buttonRelatingToLinkedFieldValue?.options ?? [];
    }
  }

  useEffect(() => {
    // There is only ONE OPTION that this relating button relates to
    // the select field. And if that is the case we consider it a given, that this
    // button is selected, ergo we call the onChange handler for this:

    // AGAIN I DO NOT TAKE ANY RESPONSIBILITY FOR THIS CODE. I DID NOT FUCK THIS UP
    // I ONLY FAILED TO CLEAN IT
    const optionsRelatingToLinkedFieldValue =
      (field.options || []).find(({ label }) => label === linkedValue)?.options ?? [];

    if (optionsRelatingToLinkedFieldValue.length === 1) {
      const v = optionsRelatingToLinkedFieldValue[0].value;

      onChange(field.name, { value: { raw: v, normalized: v }, isValid: true, hint: undefined, slideId });
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [linkedValue]);

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
    isHidden ? 'd-none' : '',
  ];

  const hint = fromTemplate(fieldHint || field.hint || '');

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        <label
          id={`${htmlId}_legend`}
          htmlFor={htmlId}
          className={isRequired ? 'required' : ''}
          onClick={handleLabelClick}
        >
          <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
            {translate(field.label)}
          </ReactMarkdown>
        </label>

        <div className="button-group" role="radiogroup" aria-labelledby={`${htmlId}_legend`}>
          {buttons.map((buttonDefinition, index) => {
            const cssClasses = ['select-option', 'col-' + (buttonDefinition.width || colWidth)];
            const buttonId = `${htmlId}_${index}`;

            const handleButtonLabelClick = (
              e: ReactMouseEvent<HTMLLabelElement> | ReactKeyboardEvent<HTMLLabelElement>,
              targetButtonId: string
            ) => {
              const isAlreadySelected = String(buttonDefinition.value) === value;

              // Keyboard activation: only act on Space/Enter
              if ('key' in e) {
                const key = e.key;
                const isActivation = key === ' ' || key === 'Spacebar' || key === 'Enter' || key === 'NumpadEnter';
                if (!isActivation) {
                  return; // Do nothing for Tab/Arrow/etc.; just let focus move
                }
                e.preventDefault();
                const input = document.getElementById(targetButtonId) as HTMLInputElement | null;
                input?.click();
                (e.currentTarget as HTMLLabelElement).focus();

                // Re-activating the selected option does not emit onChange, trigger action manually.
                if (isAlreadySelected) {
                  if (buttonDefinition.click) {
                    handleUiEvent(buttonDefinition.click);
                  }
                }
                return;
              }

              // Mouse-clicking the selected option does not emit onChange, trigger action manually.
              if (isAlreadySelected) {
                if (buttonDefinition.click) {
                  handleUiEvent(buttonDefinition.click);
                }
              }
            };

            const handleButtonChange: ChangeEventHandler<HTMLInputElement> = (e) => {
              if (e.target.checked) {
                const fieldValue = {
                  raw: buttonDefinition.value,
                  normalized: buttonDefinition.value,
                };

                const { hint, isValid } = validateField(field.validator, fieldValue);

                onChange(field.name, { value: fieldValue, hint, isValid, slideId });
                if (buttonDefinition.click) {
                  handleUiEvent(buttonDefinition.click);
                }
              }
            };

            return (
              <div key={index} className={cssClasses.join(' ')}>
                <input
                  type="radio"
                  name={'options_' + field.name}
                  id={buttonId}
                  value={String(buttonDefinition?.value)}
                  checked={String(buttonDefinition.value) === value}
                  onChange={handleButtonChange}
                  tabIndex={-1}
                />
                {buttonDefinition.label && (
                  <label
                    id={`${buttonId}_label`}
                    htmlFor={buttonId}
                    onClick={(e) => {
                      handleButtonLabelClick(e, buttonId);
                      // Ensure the visible button gets focus when clicked
                      (e.currentTarget as HTMLLabelElement).focus();
                    }}
                    role="radio"
                    aria-checked={String(buttonDefinition.value) === value}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      const key = e.key;

                      // Activation: Enter/Space select the focused option
                      if (key === ' ' || key === 'Spacebar' || key === 'Enter' || key === 'NumpadEnter') {
                        handleButtonLabelClick(e, buttonId);
                        return;
                      }

                      // Tab: move focus between options but don't select
                      if (key === 'Tab') {
                        const count = buttons.length;
                        if (!e.shiftKey) {
                          const nextIndex = index + 1;
                          if (nextIndex < count) {
                            e.preventDefault();
                            const nextLabel = document.getElementById(
                              `${htmlId}_${nextIndex}_label`
                            ) as HTMLLabelElement | null;
                            nextLabel?.focus();
                          }
                        } else {
                          const prevIndex = index - 1;
                          if (prevIndex >= 0) {
                            e.preventDefault();
                            const prevLabel = document.getElementById(
                              `${htmlId}_${prevIndex}_label`
                            ) as HTMLLabelElement | null;
                            prevLabel?.focus();
                          }
                        }
                        return;
                      }

                      // Arrow keys: rove focus only; do not select
                      if (key === 'ArrowRight' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowUp') {
                        e.preventDefault();
                        const dir = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;
                        const count = buttons.length;
                        const nextIndex = (index + dir + count) % count;
                        const nextLabel = document.getElementById(
                          `${htmlId}_${nextIndex}_label`
                        ) as HTMLLabelElement | null;
                        nextLabel?.focus();
                      }
                    }}
                  >
                    <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
                      {translate(buttonDefinition.label)}
                    </ReactMarkdown>
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
